import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { ScrollArea } from "../components/ui/scroll-area";
import { motion } from "framer-motion";
import { 
  TrendingUp, TrendingDown, FileText, Users, Calendar, 
  Euro, Receipt, MinusCircle, BarChart3, List, Loader2, Paperclip,
  AlertTriangle, AlertCircle, CheckCircle, Clock, Target, Settings, X
} from "lucide-react";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, 
  Tooltip, ResponsiveContainer, Legend, ReferenceLine, ComposedChart
} from "recharts";
import PDFViewerModal from "../components/PDFViewerModal";
import BillingGapsWidget from "../components/BillingGapsWidget";
import RenewalAlertsWidget from "../components/RenewalAlertsWidget";
import ClientRenewalsWidget from "../components/ClientRenewalsWidget";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (value) => {
  return new Intl.NumberFormat('fr-FR', { 
    style: 'currency', 
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

const formatMonth = (month) => {
  const months = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];
  return months[parseInt(month) - 1] || month;
};

// Composant KPI avec flèche de tendance
const KPICard = ({ title, value, subtitle, icon: Icon, trend, trendValue, delay = 0, action, actionLabel, variant = "default" }) => {
  const getTrendColor = () => {
    if (!trendValue || trendValue === 0) return "text-slate-400";
    return trendValue > 0 ? "text-emerald-600" : "text-red-500";
  };
  
  const getTrendBg = () => {
    if (variant === "danger") return "bg-red-50";
    if (variant === "warning") return "bg-amber-50";
    if (variant === "success") return "bg-emerald-50";
    if (trend === 'up') return 'bg-emerald-50';
    if (trend === 'down') return 'bg-red-50';
    return 'bg-blue-50';
  };
  
  const getIconColor = () => {
    if (variant === "danger") return "text-red-500";
    if (variant === "warning") return "text-amber-600";
    if (variant === "success") return "text-emerald-600";
    if (trend === 'up') return 'text-emerald-600';
    if (trend === 'down') return 'text-red-500';
    return 'text-blue-600';
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay }}
    >
      <Card className="bg-white border card-hover h-full">
        <CardContent className="p-4">
          <div className="flex items-start justify-between">
            <div className="space-y-1 min-w-0 flex-1">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground truncate">
                {title}
              </p>
              <div className="flex items-baseline gap-2">
                <p className="text-xl font-bold text-foreground font-mono">
                  {value}
                </p>
                {trendValue !== undefined && trendValue !== null && (
                  <div className={`flex items-center text-xs font-medium ${getTrendColor()}`}>
                    {trendValue > 0 ? (
                      <TrendingUp className="w-3 h-3 mr-0.5" />
                    ) : trendValue < 0 ? (
                      <TrendingDown className="w-3 h-3 mr-0.5" />
                    ) : null}
                    {trendValue !== 0 && (
                      <span>{trendValue > 0 ? "+" : ""}{trendValue.toFixed(1)}%</span>
                    )}
                  </div>
                )}
              </div>
              {subtitle && (
                <p className="text-xs text-muted-foreground">{subtitle}</p>
              )}
              {action && (
                <Button 
                  variant="link" 
                  size="sm" 
                  className="h-auto p-0 text-xs text-blue-600 hover:text-blue-800"
                  onClick={action}
                >
                  <List className="w-3 h-3 mr-1" />
                  {actionLabel || "Voir le détail"}
                </Button>
              )}
            </div>
            <div className={`p-2 rounded-lg shrink-0 ${getTrendBg()}`}>
              <Icon className={`w-4 h-4 ${getIconColor()}`} />
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="custom-tooltip">
        <p className="text-sm font-medium text-foreground mb-2">{label}</p>
        {payload.map((entry) => (
          <p key={entry.name} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: {formatCurrency(entry.value)}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export default function Dashboard() {
  const { token } = useAuth();
  const [overview, setOverview] = useState(null);
  const [monthlyData, setMonthlyData] = useState([]);
  const [comparisonData, setComparisonData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [selectedMonth, setSelectedMonth] = useState("all");
  const [availableYears, setAvailableYears] = useState([]);
  
  // Sales target state
  const [salesTarget, setSalesTarget] = useState(10000);
  const [showTargetModal, setShowTargetModal] = useState(false);
  const [tempTarget, setTempTarget] = useState(10000);
  
  // Month names for filter
  const months = [
    { value: "01", label: "Janvier" },
    { value: "02", label: "Février" },
    { value: "03", label: "Mars" },
    { value: "04", label: "Avril" },
    { value: "05", label: "Mai" },
    { value: "06", label: "Juin" },
    { value: "07", label: "Juillet" },
    { value: "08", label: "Août" },
    { value: "09", label: "Septembre" },
    { value: "10", label: "Octobre" },
    { value: "11", label: "Novembre" },
    { value: "12", label: "Décembre" }
  ];
  
  // Invoice list modal state
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [yearInvoices, setYearInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  
  // PDF viewer state
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);

  // Handle invoice click
  const handleInvoiceClick = (invoice) => {
    setSelectedInvoice(invoice);
    if (invoice.has_pdf) {
      setPdfViewerOpen(true);
    }
  };

  // Function to load invoices for the selected period (year + optional month)
  const loadPeriodInvoices = async () => {
    try {
      setLoadingInvoices(true);
      setInvoiceModalOpen(true);
      
      const headers = { Authorization: `Bearer ${token}` };
      const monthParam = selectedMonth !== "all" ? `&month=${selectedMonth}` : "";
      const res = await axios.get(`${API}/invoices?year=${selectedYear}${monthParam}&limit=10000`, { headers });
      
      // Sort by created_at (import order) - most recent first
      const sorted = (res.data.invoices || []).sort((a, b) => {
        const dateA = new Date(a.created_at || 0);
        const dateB = new Date(b.created_at || 0);
        return dateB - dateA;
      });
      
      setYearInvoices(sorted);
    } catch (error) {
      console.error("Error loading invoices:", error);
    } finally {
      setLoadingInvoices(false);
    }
  };

  // Fetch available years on mount
  useEffect(() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let y = currentYear; y >= 2020; y--) {
      years.push(y.toString());
    }
    setAvailableYears(years);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const headers = { Authorization: `Bearer ${token}` };
        
        // Build query params
        const monthParam = selectedMonth !== "all" ? `&month=${selectedMonth}` : "";
        
        const [overviewRes, monthlyRes, comparisonRes, targetRes] = await Promise.all([
          axios.get(`${API}/stats/overview?year=${selectedYear}${monthParam}`, { headers }),
          axios.get(`${API}/stats/monthly?year=${selectedYear}`, { headers }),
          axios.get(`${API}/stats/yearly-comparison?year=${selectedYear}`, { headers }),
          axios.get(`${API}/stats/sales-target`, { headers }).catch(() => ({ data: { target: 10000 } }))
        ]);
        
        setOverview(overviewRes.data);
        setSalesTarget(targetRes.data.target || 10000);
        setTempTarget(targetRes.data.target || 10000);
        
        // Format monthly data
        const monthly = monthlyRes.data.data ? monthlyRes.data.data.map(d => ({
          name: formatMonth(d.month),
          month: d.month,
          "CA HT": d.factures_ht,
          "Avoirs": Math.abs(d.avoirs_ht),
          "Net HT": d.net_ht
        })) : [];
        setMonthlyData(monthly);
        
        // Format comparison data - use the selected year and previous year
        const compYears = Object.keys(comparisonRes.data).sort();
        const allMonths = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];
        const comparison = allMonths.map(m => {
          const item = { name: formatMonth(m) };
          compYears.forEach(y => {
            item[y] = comparisonRes.data[y][m] || 0;
          });
          return item;
        });
        setComparisonData(comparison);
        
      } catch (error) {
        console.error("Failed to fetch dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [token, selectedYear, selectedMonth]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(7)].map((_, i) => (
            <div key={i} className="h-28 skeleton rounded-lg"></div>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="h-80 skeleton rounded-lg"></div>
          <div className="h-80 skeleton rounded-lg"></div>
        </div>
      </div>
    );
  }

  const prevYear = parseInt(selectedYear) - 1;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-foreground">
            Vue d'ensemble
          </h1>
          <p className="text-muted-foreground text-sm">
            Statistiques de l'année sélectionnée {selectedMonth !== "all" && <span className="text-primary">({months.find(m => m.value === selectedMonth)?.label})</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-muted-foreground" />
          <Select value={selectedYear} onValueChange={setSelectedYear}>
            <SelectTrigger className="w-32" data-testid="year-selector">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {availableYears.map(year => (
                <SelectItem key={year} value={year}>{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={selectedMonth} onValueChange={setSelectedMonth}>
            <SelectTrigger className="w-36" data-testid="month-selector">
              <SelectValue placeholder="Tous mois" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous mois</SelectItem>
              {months.map(m => (
                <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Billing gaps widget (auto-hides when no gaps) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <BillingGapsWidget compact />
        <RenewalAlertsWidget horizonDays={90} compact />
      </div>

      {/* Contrats client à surveiller (auto-hides if none) */}
      <ClientRenewalsWidget horizonDays={90} compact />

      {/* KPI Cards - Row 1 */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KPICard
          title="CA Total HT"
          value={formatCurrency(overview?.ca_ht || 0)}
          icon={Euro}
          trendValue={overview?.ca_trend}
          delay={0}
          action={loadPeriodInvoices}
          actionLabel={selectedMonth !== "all" ? `Voir les factures ${months.find(m => m.value === selectedMonth)?.label} ${selectedYear}` : `Voir les factures ${selectedYear}`}
        />
        <KPICard
          title="Factures émises"
          value={overview?.invoice_count || 0}
          subtitle="hors avoirs"
          icon={FileText}
          trendValue={overview?.count_trend}
          delay={0.05}
        />
        <KPICard
          title="CA Net HT"
          value={formatCurrency(overview?.ca_net_ht || 0)}
          subtitle="factures - avoirs"
          icon={TrendingUp}
          trend="up"
          delay={0.1}
        />
        <KPICard
          title="Avoirs émis"
          value={formatCurrency(Math.abs(overview?.avoirs_ht || 0))}
          subtitle={`${overview?.avoirs_count || 0} avoirs`}
          icon={MinusCircle}
          variant="warning"
          delay={0.15}
        />
      </div>

      {/* KPI Cards - Row 2 (Nouveaux indicateurs) */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KPICard
          title="Encours impayés"
          value={formatCurrency(overview?.encours_impayes || 0)}
          subtitle={`${overview?.impayes_count || 0} factures échues`}
          icon={AlertTriangle}
          variant={(overview?.encours_impayes || 0) > 10000 ? "danger" : "warning"}
          delay={0.2}
        />
        <KPICard
          title="Délai moyen paiement"
          value={`${overview?.delai_moyen_paiement || 0} jours`}
          subtitle="sur l'année"
          icon={Clock}
          delay={0.25}
        />
        <KPICard
          title="Taux de recouvrement"
          value={`${overview?.taux_recouvrement || 0}%`}
          subtitle={`${formatCurrency(overview?.montant_encaisse || 0)} / ${formatCurrency(overview?.montant_facture || 0)}`}
          icon={CheckCircle}
          variant={(overview?.taux_recouvrement || 0) >= 80 ? "success" : (overview?.taux_recouvrement || 0) >= 60 ? "warning" : "danger"}
          delay={0.3}
        />
        <KPICard
          title="Clients facturés"
          value={overview?.unique_clients || 0}
          subtitle="clients uniques"
          icon={Users}
          delay={0.35}
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Monthly CA Evolution with Target Line */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.45 }}
        >
          <Card className="bg-white border">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-blue-600" />
                  Évolution du CA mensuel
                </CardTitle>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-7 px-2 text-xs"
                  onClick={() => setShowTargetModal(true)}
                >
                  <Target className="w-3.5 h-3.5 mr-1" />
                  Objectif: {formatCurrency(salesTarget)}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={monthlyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis 
                      dataKey="name" 
                      tick={{ fontSize: 11 }} 
                      tickLine={false}
                      axisLine={{ stroke: '#e5e7eb' }}
                    />
                    <YAxis 
                      tick={{ fontSize: 11 }} 
                      tickLine={false}
                      axisLine={{ stroke: '#e5e7eb' }}
                      tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend 
                      wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }}
                    />
                    <ReferenceLine 
                      y={salesTarget} 
                      stroke="#22c55e" 
                      strokeDasharray="5 5" 
                      strokeWidth={2}
                      label={{ value: 'Objectif', position: 'right', fontSize: 10, fill: '#22c55e' }}
                    />
                    <Bar dataKey="CA HT" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                    <Line 
                      type="monotone" 
                      dataKey="Net HT" 
                      stroke="#10b981" 
                      strokeWidth={2}
                      dot={{ fill: '#10b981', strokeWidth: 2, r: 3 }}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Year Comparison */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.4 }}
        >
          <Card className="bg-white border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm sm:text-base font-semibold flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-blue-600" />
                <span className="hidden sm:inline">Comparaison</span> {prevYear} vs {selectedYear}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-2 sm:p-6">
              <div className="h-56 sm:h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={comparisonData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="name" stroke="#6b7280" fontSize={11} tick={{ fontSize: 10 }} />
                    <YAxis stroke="#6b7280" fontSize={11} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} width={35} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    <Bar dataKey={prevYear.toString()} fill="#94a3b8" radius={[4, 4, 0, 0]} />
                    <Bar dataKey={selectedYear} fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Invoice List Modal */}
      <Dialog open={invoiceModalOpen} onOpenChange={setInvoiceModalOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh]" data-testid="invoices-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              Factures {selectedMonth !== "all" ? `${months.find(m => m.value === selectedMonth)?.label} ` : ""}{selectedYear}
              <Badge variant="secondary" className="ml-2">{yearInvoices.length} factures</Badge>
            </DialogTitle>
          </DialogHeader>
          
          {loadingInvoices ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : (
            <ScrollArea className="h-[60vh]">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="text-xs uppercase font-medium">#</TableHead>
                    <TableHead className="text-xs uppercase font-medium">N° Facture</TableHead>
                    <TableHead className="text-xs uppercase font-medium">Type</TableHead>
                    <TableHead className="text-xs uppercase font-medium">Client</TableHead>
                    <TableHead className="text-xs uppercase font-medium">Date</TableHead>
                    <TableHead className="text-xs uppercase font-medium text-right">Total HT</TableHead>
                    <TableHead className="text-xs uppercase font-medium text-right">Total TTC</TableHead>
                    <TableHead className="text-xs uppercase font-medium">Importée le</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {yearInvoices.map((inv, index) => (
                    <TableRow 
                      key={inv.numero} 
                      className={`hover:bg-blue-50 ${inv.has_pdf ? 'cursor-pointer' : ''}`}
                      onClick={() => handleInvoiceClick(inv)}
                      data-testid={`invoice-row-${index}`}
                    >
                      <TableCell className="text-xs text-muted-foreground">{index + 1}</TableCell>
                      <TableCell className="font-mono text-sm">
                        <div className="flex items-center gap-2">
                          {inv.has_pdf && <Paperclip className="w-3 h-3 text-blue-500" />}
                          <span className="text-blue-600 hover:underline">{inv.numero}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant="outline" 
                          className={`text-xs ${inv.type === 'Facture' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'}`}
                        >
                          {inv.type}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate text-sm" title={inv.client}>
                        {inv.client}
                        {inv.sous_client && (
                          <span className="text-xs text-blue-600 block">↳ {inv.sous_client}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm whitespace-nowrap">
                        {inv.date ? new Date(inv.date).toLocaleDateString('fr-FR', { timeZone: 'Europe/Paris' }) : '-'}
                      </TableCell>
                      <TableCell className="font-mono text-sm text-right">
                        {formatCurrency(inv.total_ht || 0)}
                      </TableCell>
                      <TableCell className="font-mono text-sm text-right font-medium">
                        {formatCurrency(inv.total_ttc || 0)}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                        {inv.created_at ? new Date(inv.created_at).toLocaleDateString('fr-FR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          timeZone: 'Europe/Paris'
                        }) : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              
              {yearInvoices.length === 0 && (
                <div className="py-12 text-center">
                  <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-muted-foreground">Aucune facture pour l'année {selectedYear}</p>
                </div>
              )}
            </ScrollArea>
          )}
        </DialogContent>
      </Dialog>

      {/* PDF Viewer Modal */}
      <PDFViewerModal
        open={pdfViewerOpen}
        onOpenChange={setPdfViewerOpen}
        invoiceNumero={selectedInvoice?.numero}
        invoiceClient={selectedInvoice?.client}
      />

      {/* Target Configuration Modal */}
      <Dialog open={showTargetModal} onOpenChange={setShowTargetModal}>
        <DialogContent className="max-w-sm" data-testid="target-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="w-5 h-5 text-emerald-600" />
              Objectif mensuel
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-muted-foreground">
              Définissez votre objectif de CA mensuel. Une ligne verte sera affichée sur le graphique.
            </p>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                value={tempTarget}
                onChange={(e) => setTempTarget(Number(e.target.value))}
                className="font-mono"
                placeholder="10000"
                data-testid="target-input"
              />
              <span className="text-sm text-muted-foreground">€</span>
            </div>
            <div className="flex gap-2 justify-end">
              <Button 
                variant="outline" 
                onClick={() => setShowTargetModal(false)}
              >
                Annuler
              </Button>
              <Button 
                onClick={async () => {
                  try {
                    const headers = { Authorization: `Bearer ${token}` };
                    await axios.put(`${API}/stats/sales-target?target=${tempTarget}`, {}, { headers });
                    setSalesTarget(tempTarget);
                    setShowTargetModal(false);
                  } catch (error) {
                    console.error("Failed to save target:", error);
                  }
                }}
                data-testid="save-target-btn"
              >
                Enregistrer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
